import useEmailValidation from './useEmailValidation'

import { useEffect, useState } from 'react'

import { useFetchAndLoad } from 'hooks'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { registerUser, sendEmailValidation } from 'services/auth.service'

export default function EmailValidation() {
  const navigate = useNavigate()
  const { t } = useTranslation('emailValidation')
  const { loading: registering, callEndpoint } = useFetchAndLoad()
  const [resending, setResending] = useState(false)
  const [isCompletingRegistration, setIsCompletingRegistration] =
    useState(false)

  const storedData = localStorage.getItem('pendingRegistration')
  const registrationData = storedData ? JSON.parse(storedData) : null

  const { isValidated, checkValidation } = useEmailValidation(
    registrationData?.email || '',
  )

  const handleResendEmail = async () => {
    if (!registrationData) return

    try {
      setResending(true)
      await callEndpoint(
        sendEmailValidation(
          registrationData.email,
          registrationData.companyUuid,
        ),
      )
      toast.success(t('resend.success'), { theme: 'colored' })
    } catch (error) {
      console.error('Error resending email:', error)
      toast.error(t('resend.error'), { theme: 'colored' })
    } finally {
      setResending(false)
    }
  }

  const handleManualCheck = async () => {
    const isValid = await checkValidation()

    if (isValid) {
      toast.success(t('validated.title'), { theme: 'colored' })
    } else {
      toast.info(t('pending.notYetValidated'), { theme: 'colored' })
    }
  }

  const completeRegistration = async () => {
    if (!registrationData) return

    try {
      setIsCompletingRegistration(true)

      await callEndpoint(
        registerUser({
          email: registrationData.email,
          first_name: registrationData.firstName,
          last_name: registrationData.lastName,
          password: registrationData.password,
          subgroup: registrationData.groupUuid,
          validation_code: registrationData.validationCode,
          referral_code: registrationData.referralCode,
        }),
      )

      localStorage.removeItem('pendingRegistration')
      toast.success(t('success.message'), { theme: 'colored' })

      setTimeout(() => {
        navigate('/qr')
      }, 1500)
    } catch (error) {
      console.error('Error completing registration:', error)
      toast.error(t('error.registration_failed'), { theme: 'colored' })
      setIsCompletingRegistration(false)
    }
  }

  useEffect(() => {
    if (isValidated && registrationData) {
      completeRegistration()
    }
  }, [isValidated])

  if (!registrationData && !isCompletingRegistration) {
    navigate('/register')
    return null
  }

  if (!registrationData && isCompletingRegistration) {
    return null
  }

  const accentColor = registrationData.accentColor || '#4CAF50'

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {isValidated ? (
          <div className="text-center">
            <div className="mb-6">
              <div
                className="w-20 h-20 rounded-full mx-auto flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <svg
                  className="w-12 h-12"
                  style={{ color: accentColor }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-4">{t('validated.title')}</h2>
            <p className="text-gray-600 mb-6">{t('validated.message')}</p>
            {registering && (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <div className="mb-6">
              <div
                className="w-20 h-20 rounded-full mx-auto flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <svg
                  className="w-12 h-12"
                  style={{ color: accentColor }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">{t('pending.title')}</h2>
            <p className="text-gray-600 mb-2">{t('pending.message')}</p>
            <p className="text-sm text-gray-500 mb-8">
              {t('pending.email')}: <strong>{registrationData.email}</strong>
            </p>

            <div className="space-y-3">
              <button
                onClick={handleManualCheck}
                className="w-full py-3 rounded-full text-white font-semibold transition-all hover:brightness-110"
                style={{ backgroundColor: accentColor }}
              >
                {t('buttons.check')}
              </button>

              <button
                onClick={handleResendEmail}
                disabled={resending}
                className="w-full py-3 rounded-full border-2 font-semibold transition-all hover:bg-gray-50 disabled:opacity-50"
                style={{ borderColor: accentColor, color: accentColor }}
              >
                {resending ? t('buttons.resending') : t('buttons.resend')}
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-6">
              {t('pending.autoCheck')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
